import { payPeriodApi } from 'Api/payPeriod';
import { timeLogsApi } from 'Api/timeLogs';
import TableActionHeader from 'components/Global/TableActionHeader';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { TablePagination, TableSorter } from 'interfaces/global.interface';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { invalidText, toastText } from 'utils/utils';
import { fetchConstantDropdownAction } from '../../redux/actions/constantDropdownAction';
import {
	initializeWithCurrentPayPeriod,
	setFilters,
	setInitialized,
	setPayPeriods,
	setPayPeriodsLoading,
	setTimeActivities,
	setTimeActivitiesLoading,
	syncPayPeriodIndex,
} from '../../redux/slices/dailyAttendanceSlice';
import TimeLogsTableHeader from './Header';
import TimeLogsTable from './TimeLogsTable';
import styles from './index.module.scss';
import { Button, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PermissionContext } from 'components/Global/AuthLayout';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const TimeLogsComponent = () => {
	const dispatch = useDispatch<AppDispatch>();
	const [isSupervisorLoggedIn, setIsSupervisorLoggedIn] = useState<any>(null);
	// Redux selectors
	const { filters, data, loading, initialized } = useSelector(
		(state: any) => state.dailyAttendance
	);

	const { isLoading: dropdownOptionsLoading } = useSelector(
		(state: any) => state.constantDropdown
	);

	const dropdownOptions = useSelector(
		(state: any) => state.constantDropdown.data
	);

	const permissionContext = useContext(PermissionContext);

	const isAdd =
		permissionContext.allowedPermissions.includes('Add_Time_Logs');

	const isEdit =
		permissionContext.allowedPermissions.includes('Edit_Time_Logs');

	// Destructure for easier access
	const {
		currentPage,
		pageSize,
		sortField,
		sortOrder,
		date,
		statusFilterValue,
		currentPayPeriodIndex,
	} = filters;

	const { timeActivities, totalRecords, payPeriods } = data;
	const {
		timeActivities: timeActivitiesLoading,
		payPeriods: payPeriodsLoading,
	} = loading;

	const navigate = useNavigate();

	// Helper functions to update filters
	const updateFilters = (newFilters: Partial<typeof filters>) => {
		dispatch(setFilters(newFilters));
	};

	const handleDatePicker = (value: [string, string]) => {
		updateFilters({
			date: value,
			currentPage: 1,
		});
	};

	const handlePayPeriodChange = (index: number, payPeriod: any) => {
		updateFilters({
			currentPayPeriodIndex: index,
			date: [payPeriod.startDate, payPeriod.endDate],
			currentPage: 1,
		});
	};

	const tableChangeHandler = (
		pagination: TablePagination,
		filters: any,
		sorter: TableSorter
	) => {
		let newSortOrder = '';
		if (sorter?.order === 'ascend') {
			newSortOrder = 'asc';
		} else if (sorter?.order) {
			newSortOrder = 'desc';
		}

		const newSortField =
			sorter.field === 'submittedOn' ? 'createdAt' : sorter.field;

		updateFilters({
			currentPage: pagination.current,
			pageSize: pagination.pageSize,
			sortOrder: newSortOrder,
			sortField: newSortField,
		});
	};

	const handleStatusFilter = (value: string) => {
		updateFilters({
			statusFilterValue: value,
			currentPage: 1,
		});
	};

	const payPeriodsForNavigation = [...payPeriods].sort(
		(a, b) =>
			new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
	);

	// Reset filters function
	// const clearFilters = () => {
	// 	dispatch(resetFilters());
	// };

	const fetchTimeLogs = async () => {
		dispatch(setTimeActivitiesLoading(true));
		try {
			const query = {
				supervisorId: statusFilterValue,
				startDate: dayjs(date[0]).format('YYYY-MM-DD'),
				endDate: dayjs(date[1]).format('YYYY-MM-DD'),
				sortBy: sortField,
				sortOrder: sortOrder,
				isExport: true,
			};

			const response = await timeLogsApi.getTimeActivities(query);
			const responseData = response.data.data || [];
			setIsSupervisorLoggedIn(response.data.supervisorId);

			dispatch(
				setTimeActivities({
					timeActivities: responseData,
					totalRecords: responseData.length,
				})
			);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching Daily Attendance';
			toastText(errorMessage, 'error');

			dispatch(
				setTimeActivities({
					timeActivities: [],
					totalRecords: 0,
				})
			);
		}
		dispatch(setTimeActivitiesLoading(false));
	};

	const payPeriodDateOptions = async () => {
		dispatch(setPayPeriodsLoading(true));
		try {
			const response = await payPeriodApi.getPayPeriodDateOptions();
			const responseData = response.data.data || [];

			dispatch(setPayPeriods(responseData));

			if (responseData.length > 0) {
				if (initialized) {
					// If already initialized (returning from another screen), sync the index
					dispatch(
						syncPayPeriodIndex({
							payPeriods: responseData,
							currentDate: date,
						})
					);
				} else {
					// First time initialization, set current pay period
					dispatch(initializeWithCurrentPayPeriod(responseData));
				}
			}

			dispatch(setInitialized(true));
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching Pay Periods';

			toastText(errorMessage, 'error');
			dispatch(setInitialized(true));
		}
		dispatch(setPayPeriodsLoading(false));
	};

	const handleClick = () => {
		const url = `/time-logs?isDateDisable=false&date=${dayjs(
			date[0]
		).format('YYYY-MM-DD')}${
			invalidText(statusFilterValue)
				? ''
				: `&supervisor=${statusFilterValue}`
		}`;
		navigate(url, {
			state: { from: '/daily-attendance' },
		});
	};

	// Fetch dropdowns once
	useEffect(() => {
		dispatch(fetchConstantDropdownAction({}));
	}, [dispatch]);

	// Fetch pay periods on mount
	useEffect(() => {
		payPeriodDateOptions();
	}, []);

	// Fetch time logs when filters change and component is initialized
	useEffect(() => {
		if (date[0] && date[1] && initialized) {
			fetchTimeLogs();
		}
	}, [date, statusFilterValue, sortField, sortOrder, initialized]);

	// Paginate data in the frontend
	const paginatedData = useMemo(() => {
		const start = (currentPage - 1) * pageSize;
		return timeActivities.slice(start, start + pageSize);
	}, [timeActivities, currentPage, pageSize]);

	return (
		<>
			<div
				style={{
					maxHeight: 'calc(100vh - 100px)',
				}}
			>
				{' '}
				<div className={styles['daily-attendance-header']}>
					<p>Daily Attendance</p>

					{(isAdd || isEdit) && (
						<Space>
							<Button type="primary" onClick={handleClick}>
								Add Time Logs
							</Button>
						</Space>
					)}
				</div>
			</div>
			<div className={styles['leave-request-container']}>
				<TimeLogsTableHeader
					statusFilterValue={statusFilterValue}
					handleStatusFilter={handleStatusFilter}
					options={dropdownOptions?.supervisors}
					handleDatePicker={handleDatePicker}
					dateValue={date}
					dropdownOptionsLoading={dropdownOptionsLoading}
					payPeriodOptions={payPeriods}
					payPeriodLoading={payPeriodsLoading}
					currentPayPeriodIndex={currentPayPeriodIndex}
					payPeriodsForNavigation={payPeriodsForNavigation}
					onPayPeriodChange={handlePayPeriodChange}
					supervisorId={isSupervisorLoggedIn}
					addPermission={isAdd || isEdit}
				/>

				<TimeLogsTable
					tableData={paginatedData}
					totalRecords={totalRecords}
					currentPage={currentPage}
					pageSize={pageSize}
					tableChangeHandler={tableChangeHandler}
					isLoading={timeActivitiesLoading}
					statusFilterValue={statusFilterValue}
				/>
			</div>
		</>
	);
};

export default TimeLogsComponent;
